import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import Switch from 'react-switch'
import { fetch_all_permissions } from '../../api/permissionsAPI'
import { create_new_role, delete_role_by_id, duplicate_role_by_id, fetch_role_by_id, update_role_by_id } from '../../api/roleAPI'
import config from '../../app-config'
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification'
import SubNavbar from '../../components/ui/SubNavbar'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

export default function RolePermissionFormPage() {
  const navigate = useNavigate()
  const role_id = new URLSearchParams(window.location.search).get("role_id")
  const [permissionsData, setPermissionsData] = useState([])
  const [searchQuery, setSearchQuery] = useState("")

  const [activeTab, setActiveTab] = useState("general") // 'general', 'access_scopes', 'permissions'

  // Resource options lists
  const [availableServices, setAvailableServices] = useState([])
  const [availableMinistries, setAvailableMinistries] = useState([])
  const [availableResources, setAvailableResources] = useState([])
  const [availableEvents, setAvailableEvents] = useState([])
  const [availableMissions, setAvailableMissions] = useState([])
  const [availableDGs, setAvailableDGs] = useState([])
  const [availableClusters, setAvailableClusters] = useState([])
  const [availableUsers, setAvailableUsers] = useState([])

  const defaultResourceAccess = {
    MinistryModel: ["*"],
    ConfigServiceModel: ["*"],
    ResourcesModel: ["*"],
    EventModel: ["*"],
    MissionsModel: ["*"],
    DiscipleGroupModel: ["*"],
    DiscipleGroupClusterModel: ["*"],
    UserAccountModel: ["*"]
  }

  const [roleData, setRoleData] = useState({
    name: '',
    permissions: [],
    resource_access: defaultResourceAccess,
    is_super_admin: '0',
    description: '',
    redirect: "/admin"
  })

  const fetchAllResourceLists = async () => {
    try {
      const resServices = await fetch(config.endpoint + "/config", { credentials: "include" })
      const dataServices = await resServices.json()
      if (dataServices?.data?.services) setAvailableServices(dataServices.data.services)

      const resMin = await fetch(config.endpoint + "/ministry", { credentials: "include" })
      const dataMin = await resMin.json()
      if (dataMin?.data) setAvailableMinistries(dataMin.data)

      const resResources = await fetch(config.endpoint + "/resources", { credentials: "include" })
      const dataRes = await resResources.json()
      if (dataRes?.data) setAvailableResources(dataRes.data)

      const resEvents = await fetch(config.endpoint + "/events", { credentials: "include" })
      const dataEv = await resEvents.json()
      if (dataEv?.data) setAvailableEvents(dataEv.data)

      const resMissions = await fetch(config.endpoint + "/missions", { credentials: "include" })
      const dataMis = await resMissions.json()
      if (dataMis?.data) setAvailableMissions(dataMis.data)

      const resDG = await fetch(config.endpoint + "/dg", { credentials: "include" })
      const dataDG = await resDG.json()
      if (dataDG?.data) setAvailableDGs(dataDG.data)

      const resClusters = await fetch(config.endpoint + "/dg/clusters", { credentials: "include" })
      const dataClusters = await resClusters.json()
      if (dataClusters?.data) setAvailableClusters(dataClusters.data)

      const resUsers = await fetch(config.endpoint + "/users", { credentials: "include" })
      const dataUsers = await resUsers.json()
      if (dataUsers?.data) setAvailableUsers(dataUsers.data)

    } catch (err) {
      console.error("Error fetching resource lists:", err)
    }
  }

  const fetchPermissions = async () => {
    let { data, error, message } = await fetch_all_permissions()
    if (error) return console.error(message)
    setPermissionsData(data)
  }

  const fetchRoleById = async () => {
    if (!role_id || role_id === 'new') return
    let { data, message, error } = await fetch_role_by_id(role_id)
    if (error) return console.error(error)

    setRoleData({
      ...data,
      resource_access: {
        ...defaultResourceAccess,
        ...(data?.resource_access || {})
      }
    })
  }

  useEffect(() => {
    fetchRoleById()
    fetchPermissions()
    fetchAllResourceLists()
  }, [role_id])

  const createNewRole = async () => {
    if (role_id !== 'new') return
    let { data, message, error } = await create_new_role(roleData)
    if (error) return notify({ message, type: "error" })
    navigate(`/admin/roles-permissions/details?role_id=${data.id}`)
    notify({ message: "Role created successfully", type: "success" })
  }

  const updateRoleById = async () => {
    if (role_id === 'new') return
    let { data, message, error } = await update_role_by_id(role_id, roleData)
    if (error) return notify({ message, type: "error" })
    fetchRoleById()
    notify({ message: "Role updated successfully", type: "success" })
  }

  const duplicateRole = async () => {
    if (role_id === 'new') return
    let { data, message, error } = await duplicate_role_by_id(role_id)
    if (error) return notify({ message, type: "error" })
    notify({ message: "Role duplicated successfully", type: "success" })
    navigate(`/admin/roles-permissions/details?role_id=${data.id}`)
  }

  const deleteRoleById = async () => {
    if (role_id === 'new') return
    let { data, message, error } = await delete_role_by_id(role_id)
    if (error) return notify({ message, type: "error" })
    notify({ message: "Role deleted successfully", type: "success" })
    navigate(-1)
  }

  const renderResourceScopeBlock = (title, subtitle, modelKey, items, nameKey = "name") => {
    const currentAccess = roleData.resource_access?.[modelKey] || ["*"]
    const isAll = currentAccess.includes("*")

    const toggleAll = () => {
      setRoleData({
        ...roleData,
        resource_access: {
          ...roleData.resource_access,
          [modelKey]: isAll ? items.map(i => String(i.id)) : ["*"]
        }
      })
    }

    const toggleItem = (itemIdStr) => {
      let updated = [...(roleData.resource_access?.[modelKey] || [])].filter(id => id !== "*")
      if (updated.includes(itemIdStr)) {
        updated = updated.filter(id => id !== itemIdStr)
      } else {
        updated.push(itemIdStr)
      }
      setRoleData({
        ...roleData,
        resource_access: {
          ...roleData.resource_access,
          [modelKey]: updated
        }
      })
    }

    return (
      <div className="space-y-3 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <label className="font-semibold text-sm">{title}</label>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          <button
            type="button"
            className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
            onClick={toggleAll}
          >
            {isAll ? "Restrict to Specific Instances" : "Grant All Access (*)"}
          </button>
        </div>

        {!isAll && (
          items.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No resource items available.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
              {items.map((item) => {
                const itemIdStr = String(item.id)
                const selected = currentAccess.includes(itemIdStr)
                return (
                  <label key={item.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 text-xs cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleItem(itemIdStr)}
                    />
                    <span className="font-medium truncate">{item[nameKey] || item.title || `${item.first_name || ''} ${item.last_name || ''}`.trim() || `ID #${item.id}`}</span>
                  </label>
                )
              })}
            </div>
          )
        )}
      </div>
    )
  }

  const renderUserAccessScopeBlock = () => {
    const userAccess = roleData.resource_access?.UserAccountModel || ["*"]
    const mode = userAccess.includes("*")
      ? "all"
      : userAccess.includes("follow_service_profile_scope")
        ? "follow_service"
        : "selected"

    const setMode = (newMode) => {
      let newAccess = ["*"]
      if (newMode === "follow_service") {
        newAccess = ["follow_service_profile_scope"]
      } else if (newMode === "selected") {
        newAccess = availableUsers.slice(0, 5).map(u => String(u.id))
      }
      setRoleData({
        ...roleData,
        resource_access: {
          ...roleData.resource_access,
          UserAccountModel: newAccess
        }
      })
    }

    const toggleSelectedUser = (userIdStr) => {
      let updated = [...(roleData.resource_access?.UserAccountModel || [])].filter(id => id !== "*" && id !== "follow_service_profile_scope")
      if (updated.includes(userIdStr)) {
        updated = updated.filter(id => id !== userIdStr)
      } else {
        updated.push(userIdStr)
      }
      setRoleData({
        ...roleData,
        resource_access: {
          ...roleData.resource_access,
          UserAccountModel: updated
        }
      })
    }

    return (
      <div className="space-y-3 pt-4 border-t border-gray-100">
        <div>
          <label className="font-semibold text-sm">User Directory Access Scope</label>
          <p className="text-xs text-gray-500">Specify whether users in this role can access all users, follow their assigned Service Profile scope, or view selected users.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-xs font-semibold ${mode === "all" ? "bg-blue-50 border-blue-400" : "border-gray-200"
            }`}>
            <input type="radio" name="user_access_mode" checked={mode === "all"} onChange={() => setMode("all")} />
            <div>
              <p className="font-bold text-gray-900">All Users (*)</p>
              <p className="text-[11px] text-gray-500">Unrestricted access to all user accounts</p>
            </div>
          </label>

          <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-xs font-semibold ${mode === "follow_service" ? "bg-blue-50 border-blue-400" : "border-gray-200"
            }`}>
            <input type="radio" name="user_access_mode" checked={mode === "follow_service"} onChange={() => setMode("follow_service")} />
            <div>
              <p className="font-bold text-gray-900">Follow Service Profile Scope</p>
              <p className="text-[11px] text-gray-500">Scope users dynamically by Service Profile</p>
            </div>
          </label>

          <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-xs font-semibold ${mode === "selected" ? "bg-blue-50 border-blue-400" : "border-gray-200"
            }`}>
            <input type="radio" name="user_access_mode" checked={mode === "selected"} onChange={() => setMode("selected")} />
            <div>
              <p className="font-bold text-gray-900">Selected Users</p>
              <p className="text-[11px] text-gray-500">Restrict access to specific user IDs</p>
            </div>
          </label>
        </div>

        {mode === "selected" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
            {availableUsers.map((user) => {
              const uIdStr = String(user.id)
              const selected = userAccess.includes(uIdStr)
              return (
                <label key={user.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 text-xs cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleSelectedUser(uIdStr)}
                  />
                  <span className="font-medium truncate">{user.first_name || ''} {user.last_name || ''} ({user.email || user.id})</span>
                </label>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const tabs = [
    { id: "general", label: "General Fields" },
    { id: "access_scopes", label: "Access Scopes" },
    { id: "permissions", label: "Permissions" }
  ]

  return (
    <AdminLayout
      navSelected={"Roles & Permissions"}
      headerText={"Role & Permissions"}
      toolbar={
        <>
          <h1 className="text-2xl font-bold text-gray-900">
            {role_id === "new" ? "Create Role" : "Edit Role"}
          </h1>
          <div className="flex gap-2">
            {role_id !== "new" && (
              <>
                <Button variant="success" onClick={duplicateRole}>
                  Duplicate
                </Button>
                <Button variant="danger" onClick={deleteRoleById}>
                  Delete
                </Button>
              </>
            )}
            <Button
              variant="primary"
              onClick={() => {
                if (role_id === "new") {
                  createNewRole()
                } else {
                  updateRoleById()
                }
              }}
            >
              {role_id === "new" ? "Create" : "Save"}
            </Button>
          </div>
        </>
      }
    >
      <div className="w-full space-y-4">
        {/* Reusable SubNavbar */}
        <SubNavbar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* TAB 1: General Fields */}
        {activeTab === "general" && (
          <Card title={role_id && role_id !== "new" ? "Edit Role Details" : "Create Role Details"}>
            <div className="space-y-4">
              <div className="input-container">
                <label className="input-label font-semibold">Role Name</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter Role name"
                  value={roleData.name}
                  onChange={(e) => setRoleData({ ...roleData, name: e.target.value })}
                />
              </div>
              <div className="input-container">
                <label className="input-label font-semibold">Redirect URL</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter Redirect URL"
                  value={roleData.redirect}
                  onChange={(e) => setRoleData({ ...roleData, redirect: e.target.value })}
                />
              </div>
              <div className="input-container flex items-center justify-between">
                <label className="input-label font-semibold">Super Admin</label>
                <Switch
                  checked={roleData.is_super_admin == '1'}
                  onChange={(checked) => setRoleData({ ...roleData, is_super_admin: checked ? '1' : '0' })}
                />
              </div>
              <div className="input-container">
                <label className="input-label font-semibold">Description</label>
                <textarea
                  className="textarea-input"
                  placeholder="A brief description of the User Role"
                  value={roleData.description}
                  onChange={(e) => setRoleData({ ...roleData, description: e.target.value })}
                />
              </div>
            </div>
          </Card>
        )}

        {/* TAB 2: Access Scopes */}
        {activeTab === "access_scopes" && (
          <Card
            title="Resource Access Scopes"
            subtitle="Configure which specific model resource instances users in this role are granted access to."
          >
            <div className="space-y-4">
              {renderUserAccessScopeBlock()}

              {renderResourceScopeBlock(
                "Ministry Access",
                "Controls access to specific Ministries and linked Ministry events/DGs.",
                "MinistryModel",
                availableMinistries
              )}

              {renderResourceScopeBlock(
                "Service Profile Access (Services, Bulletin, Announcements)",
                "Controls access to Service Profiles, Announcements, Bulletins & Runsheets.",
                "ConfigServiceModel",
                availableServices
              )}

              {renderResourceScopeBlock(
                "Resources Access",
                "Controls access to church resources and equipment bookings.",
                "ResourcesModel",
                availableResources,
                "title"
              )}

              {renderResourceScopeBlock(
                "Events Access",
                "Controls access to specific Events and Event registration forms.",
                "EventModel",
                availableEvents,
                "title"
              )}

              {renderResourceScopeBlock(
                "Missions Access",
                "Controls access to specific Mission trips and Mission rosters.",
                "MissionsModel",
                availableMissions,
                "title"
              )}

              {renderResourceScopeBlock(
                "Disciple Group Access",
                "Controls access to specific Disciple Groups (DGs).",
                "DiscipleGroupModel",
                availableDGs
              )}

              {renderResourceScopeBlock(
                "Disciple Cluster Access",
                "Controls access to specific Disciple Group Clusters.",
                "DiscipleGroupClusterModel",
                availableClusters
              )}
            </div>
          </Card>
        )}

        {/* TAB 3: Permissions */}
        {activeTab === "permissions" && (
          <Card title="Permissions">
            <div>
              <input
                type="text"
                className="text-input mb-4"
                placeholder="Search Permissions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-4">
              {Object.keys(permissionsData || {}).map((category) => {
                const categoryPermissions = Object.values(permissionsData[category] || {});
                if (!categoryPermissions.length) return null;

                const firstPermission = categoryPermissions[0];
                const matchesSearch = !searchQuery || categoryPermissions.some(p =>
                  p?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  p?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  p?.category?.toLowerCase().includes(searchQuery.toLowerCase())
                );
                if (!matchesSearch) return null;

                return (
                  <div key={category} className='mb-2'>
                    <div className="flex flex-row items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">{firstPermission?.category || category}</h3>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const keys = categoryPermissions.map(p => p.key);
                          const allSelected = keys.every(key => roleData.permissions.includes(key));
                          let updatedPermissions;
                          if (allSelected) {
                            updatedPermissions = roleData.permissions.filter(p => !keys.includes(p));
                          } else {
                            updatedPermissions = Array.from(new Set([...roleData.permissions, ...keys]));
                          }
                          setRoleData({ ...roleData, permissions: updatedPermissions });
                        }}
                      >
                        {(categoryPermissions.every(p => roleData.permissions.includes(p.key)) || roleData?.is_super_admin == 1) ? 'Unselect All' : 'Select All'}
                      </Button>
                    </div>
                    <div className="flex-col flex gap-2">
                      {Object.keys(permissionsData[category] || {}).map((permissionKey) => {
                        const permission = permissionsData[category][permissionKey];
                        if (!permission) return null;
                        if (searchQuery && !permission.name?.toLowerCase().includes(searchQuery.toLowerCase()) && !permission.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
                          return null;
                        }
                        return (
                          <div key={permission.key} className="flex items-center gap-2 justify-between">
                            <div className='flex flex-col'>
                              <label className="text-sm font-bold">{permission.name}</label>
                              <label className="input-label text-gray-500">{permission.description}</label>
                            </div>
                            <Switch
                              checked={roleData.permissions.includes(permission.key) || roleData?.is_super_admin == 1}
                              onChange={(checked) => {
                                let updatedPermissions = [...roleData.permissions]
                                if (checked) {
                                  updatedPermissions.push(permission.key)
                                } else {
                                  updatedPermissions = updatedPermissions.filter(p => p !== permission.key)
                                }
                                setRoleData({ ...roleData, permissions: updatedPermissions })
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
